import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Container,
    useTheme,
    alpha,
    Stack,
    List,
    ListItem,
    ListItemText
} from "@mui/material";
import { designTokens } from '../theme/designTokens';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PolicyIcon from '@mui/icons-material/Policy';
import { deidentificationFaqs } from '../metadata/deidentification';

const DataDeIdentification = () => {
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const renderAnswerBlock = (block, idx) => {
        if (block.type === 'segments') {
            return (
                <Typography
                    key={idx}
                    variant="body1"
                    sx={{
                        color: 'text.primary',
                        lineHeight: designTokens.lineHeight.relaxed,
                        fontSize: designTokens.fontSize.base,
                        mb: designTokens.spacing.sm / 8,
                    }}
                >
                    {block.segments.map((seg, i) =>
                        typeof seg === 'string' ? (
                            <span key={i}>{seg}</span>
                        ) : (
                            <a
                                key={i}
                                href={seg.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: theme.palette.primary.main, fontWeight: 500 }}
                            >
                                {seg.text}
                            </a>
                        )
                    )}
                </Typography>
            );
        }
        if (block.type === 'bullets') {
            return (
                <List key={idx} dense disablePadding sx={{ mt: designTokens.spacing.xs / 8, mb: designTokens.spacing.sm / 8 }}>
                    {block.items.map((item, i) => (
                        <ListItem key={i} sx={{ py: designTokens.spacing.xs / 8, px: 0, alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: 'primary.main',
                                    mt: '9px',
                                    mr: 1.5,
                                    flexShrink: 0,
                                }}
                            />
                            <ListItemText
                                primary={item}
                                primaryTypographyProps={{
                                    variant: 'body1',
                                    sx: {
                                        color: 'text.primary',
                                        lineHeight: designTokens.lineHeight.relaxed,
                                        fontSize: designTokens.fontSize.base,
                                    }
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            );
        }
        return (
            <Typography
                key={idx}
                variant="body1"
                sx={{
                    color: 'text.primary',
                    lineHeight: designTokens.lineHeight.relaxed,
                    fontSize: designTokens.fontSize.base,
                    mb: designTokens.spacing.sm / 8,
                }}
            >
                {block.text}
            </Typography>
        );
    };

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: designTokens.spacing['3xl'] / 8 }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                        p: designTokens.spacing['6xl'] / 8,
                        mb: designTokens.spacing['6xl'] / 8,
                        textAlign: 'center',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                >
                    <Stack spacing={designTokens.spacing['2xl'] / 8} alignItems="center">
                        <Box
                            sx={{
                                width: designTokens.spacing['7xl'],
                                height: designTokens.spacing['7xl'],
                                borderRadius: designTokens.borderRadius.full,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: designTokens.shadows.xl,
                            }}
                        >
                            <PolicyIcon sx={{ fontSize: designTokens.spacing['5xl'], color: 'white' }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{
                                    fontWeight: designTokens.fontWeight.bold,
                                    color: 'text.primary',
                                    mb: designTokens.spacing.lg / 8,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Data De-Identification
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: designTokens.fontWeight.normal,
                                    maxWidth: 640,
                                    mx: 'auto',
                                    lineHeight: designTokens.lineHeight.relaxed,
                                }}
                            >
                                De-Identification of Unstructured Data Through Obfuscation FAQ
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* FAQ Section */}
                <Box sx={{ mb: designTokens.spacing['6xl'] / 8 }}>
                    <Typography
                        variant="h4"
                        component="h2"
                        sx={{
                            mb: designTokens.spacing['3xl'] / 8,
                            fontWeight: designTokens.fontWeight.semibold,
                            color: 'text.primary',
                        }}
                    >
                        Common Questions
                    </Typography>

                    <Stack spacing={designTokens.spacing.lg / 8}>
                        {deidentificationFaqs.map((faq, index) => (
                            <Accordion
                                key={index}
                                expanded={expanded === `panel${index + 1}`}
                                onChange={handleChange(`panel${index + 1}`)}
                                sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    boxShadow: expanded === `panel${index + 1}`
                                        ? designTokens.shadows.xl
                                        : designTokens.shadows.sm,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: designTokens.shadows.md,
                                    },
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                color: expanded === `panel${index + 1}`
                                                    ? 'primary.main'
                                                    : 'text.secondary',
                                                transition: 'color 0.2s ease-in-out',
                                            }}
                                        />
                                    }
                                    aria-controls={`panel${index + 1}bh-content`}
                                    id={`panel${index + 1}bh-header`}
                                    sx={{
                                        backgroundColor: expanded === `panel${index + 1}`
                                            ? alpha(theme.palette.primary.main, 0.04)
                                            : 'transparent',
                                        minHeight: designTokens.spacing['7xl'],
                                        px: designTokens.spacing['2xl'] / 8,
                                        '&.Mui-expanded': {
                                            borderBottomLeftRadius: 0,
                                            borderBottomRightRadius: 0,
                                        },
                                        transition: 'background-color 0.2s ease-in-out',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: designTokens.fontWeight.medium,
                                            color: expanded === `panel${index + 1}`
                                                ? 'primary.main'
                                                : 'text.primary',
                                            transition: 'color 0.2s ease-in-out',
                                            pr: designTokens.spacing.lg / 8,
                                        }}
                                    >
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: designTokens.spacing['2xl'] / 8,
                                        py: designTokens.spacing['2xl'] / 8,
                                        backgroundColor: 'background.paper',
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    {faq.answer.map((block, idx) => renderAnswerBlock(block, idx))}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default DataDeIdentification;
